import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useMyPendingRatingsApi } from './config';
import DataTable from '../../components/ui/DataTable';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import OptionsMenu from '../../components/ui/OptionsMenu';
import GenericSelect from '../../components/ui/GenericSelect';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';

// Task 34 — ManagerRatingPage (/talent/nine-box-ratings, ['TD.Write']): "form" kaynaklı
// 9-Box süreçlerinde yöneticinin doldurması gereken bekleyen puan formları. Faz A'nın
// manuel-giriş modalıyla AYNI alan seti (1-5 puan + gerekçe) ve AYNI responseError
// anahtarları (performance.ninebox.manualScoreRange/manualReasonRequired) yeniden
// kullanılıyor — yalnız hedef artık bir NineBoxManagerRating satırı.
const Container = styled.div``;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: 8px;
  max-width: 440px;
  width: 100%;
  padding: 28px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
`;

const CardTitle = styled.h3`
  margin: 0 0 20px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const SCORE_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ id: n, name: String(n) }));

export default function ManagerRatingPage() {
  const { t } = useTranslation();
  const { data, fetching, loadFailed, submitRating } = useMyPendingRatingsApi();

  const [activeRating, setActiveRating] = useState(null); // { id, employeeName, processName }
  const [score, setScore] = useState(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const openRate = useCallback((row) => {
    setActiveRating(row);
    setScore(null);
    setReason('');
  }, []);

  const closeModal = useCallback(() => setActiveRating(null), []);

  const handleSubmit = useCallback(async () => {
    if (!activeRating || !score || !reason.trim()) return;
    setSaving(true);
    const ok = await submitRating(activeRating.id, score, reason.trim());
    if (ok) setActiveRating(null);
    setSaving(false);
  }, [activeRating, score, reason, submitRating]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeName',
      label: t('performance.potential.employee'),
      options: { filter: false, sort: false },
    },
    {
      name: 'processName',
      label: t('performance.ninebox.process.title'),
      options: { filter: false, sort: false },
    },
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          const actions = [{ type: 'edit', label: t('performance.ninebox.ratings.rate'), onClick: () => openRate(row) }];
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    },
  ], [t, data, openRate]);

  const canSave = !!score && reason.trim().length > 0 && !saving;

  return (
    <Container>
      {/* TD-23 — başarısız okuma boş tabloya DEĞİL, uyarıya düşer. */}
      {loadFailed && <LoadErrorBanner />}
      <DataTable
        title={t('performance.ninebox.ratings.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={data.length}
        pageIndex={0}
        pageSize={data.length || 10}
      />

      <Overlay $open={!!activeRating}>
        {activeRating && (
          <Card>
            <CardTitle>{t('performance.ninebox.ratings.modalTitle')}</CardTitle>
            <FieldGroup>
              <GenericSelect
                label={t('performance.ninebox.manualScore')}
                value={score}
                onChange={setScore}
                options={SCORE_OPTIONS}
                required
              />
            </FieldGroup>
            <FieldGroup>
              <TextInput
                label={t('performance.ninebox.manualReason')}
                type="textarea"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
              />
            </FieldGroup>
            <Actions>
              <Button variant="outline" onClick={closeModal} disabled={saving}>{t('common.cancel')}</Button>
              <Button onClick={handleSubmit} disabled={!canSave} loading={saving}>{t('common.save')}</Button>
            </Actions>
          </Card>
        )}
      </Overlay>
    </Container>
  );
}
